import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { 
  Users, 
  TrendingUp, 
  Clock, 
  Settings,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Facebook,
  Chrome,
  Globe,
  UserPlus,
  Calendar,
  Target,
  BarChart3,
  Filter,
  Download,
  Eye,
  Zap,
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  Edit,
  X,
  Instagram
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProspects = () => {
  const { user } = useAuth();
  const [prospects, setProspects] = useState([]);
  const [clients, setClients] = useState([]); // Pour l'onglet Prospects
  const [statistiques, setStatistiques] = useState(null);
  const [configuration, setConfiguration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false); // Pour le chargement de plus de prospects
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filter, setFilter] = useState('tous');
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [activeTab, setActiveTab] = useState('prospects_entrants'); // 'prospects_entrants', 'configuration', 'prospects'
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, total_pages: 1 });
  
  // États pour l'onglet Prospects
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // États pour le dispatching automatique
  const [dispatchingEnabled, setDispatchingEnabled] = useState(true);
  const [dispatchingStats, setDispatchingStats] = useState(null);
  
  // Fonction pour formater le numéro de téléphone français : +33612345678 → 06 12 34 56 78
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    // Retirer tous les espaces, tirets, points, parenthèses existants
    let cleaned = phone.replace(/[\s\-\.\(\)]/g, '');
    
    // Si le numéro commence par +33, remplacer par 0
    if (cleaned.startsWith('+33')) {
      cleaned = '0' + cleaned.substring(3);
    }
    
    // Si le numéro commence par 0033, remplacer par 0
    if (cleaned.startsWith('0033')) {
      cleaned = '0' + cleaned.substring(4);
    }
    
    // Enlever tous les caractères non numériques restants
    cleaned = cleaned.replace(/\D/g, '');
    
    // S'assurer qu'il commence par 0 si ce n'est pas le cas et qu'il a 9 chiffres
    if (cleaned.length === 9 && !cleaned.startsWith('0')) {
      cleaned = '0' + cleaned;
    }
    
    // Ajouter un espace tous les 2 chiffres
    // Ex: 0612345678 → 06 12 34 56 78
    if (cleaned.length === 10) {
      return cleaned.match(/.{1,2}/g).join(' ');
    }
    
    // Si le format est différent, retourner tel quel
    return phone;
  };

  // Fonction pour formater la source du lead
  // Charger la configuration du dispatching
  useEffect(() => {
    const loadDispatchingConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}/api/admin/config/dispatching`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const config = await response.json();
          setDispatchingEnabled(config.actif !== undefined ? config.actif : config.enabled);
        }
      } catch (error) {
        console.error('Erreur chargement config dispatching:', error);
      }
    };
    
    const loadDispatchingStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}/api/admin/stats/dispatching`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const stats = await response.json();
          setDispatchingStats(stats);
        }
      } catch (error) {
        console.error('Erreur chargement stats dispatching:', error);
      }
    };
    
    if (activeTab === 'prospects_entrants') {
      loadDispatchingConfig();
      loadDispatchingStats();
      
      // Rafraîchir les stats toutes les 30 secondes
      const interval = setInterval(loadDispatchingStats, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);
  
  // Fonction pour activer/désactiver le dispatching
  const toggleDispatching = async () => {
    try {
      const token = localStorage.getItem('token');
      const newState = !dispatchingEnabled;
      
      const response = await fetch(`${BACKEND_URL}/api/admin/config/dispatching`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ actif: newState })
      });
      
      if (response.ok) {
        setDispatchingEnabled(newState);
        console.log(`Dispatching automatique ${newState ? 'activé' : 'désactivé'} !`);
      }
    } catch (error) {
      console.error('Erreur toggle dispatching:', error);
      console.log('Erreur lors de la modification du dispatching');
    }
  };

  const formatSource = (source) => {
    if (!source || source.trim() === '') {
      return 'Inconnue';
    }
    
    // Mapper les sources backend vers des noms lisibles
    const sourceMap = {
      // Sources backend
      'facebook_ads': 'Facebook',
      'google_ads': 'Google Ads',
      'instagram': 'Instagram',
      'linkedin': 'LinkedIn',
      'tiktok': 'TikTok',
      'autres': 'Autres',
      'autre': 'Autre',
      // Sources techniques (au cas où)
      'Demandgen-Static': 'Google Ads',
      'demandgen-static': 'Google Ads',
      'fb': 'Facebook',
      'FB': 'Facebook'
    };
    
    return sourceMap[source] || source; // Si pas dans la map, retourner tel quel
  };
  
  // États pour dispatching manuel
  const [commerciaux, setCommerciaux] = useState([]);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [prospectToDispatch, setProspectToDispatch] = useState(null);
  const [selectedCommercial, setSelectedCommercial] = useState('');
  const [showAddClientForm, setShowAddClientForm] = useState(false);
  const [newClient, setNewClient] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    ville: '',
    code_postal: '',
    commercial_id: user?.id || '',
    surface_toit: '',
    consommation_annuelle: '',
    notes: ''
  });

  useEffect(() => {
    fetchData(1);
    fetchCommerciaux();
    // Rafraîchir toutes les 2 minutes SEULEMENT la première page
    const interval = setInterval(() => fetchData(1), 120000);
    return () => clearInterval(interval);
  }, []);
  
  const fetchCommerciaux = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/commerciaux-actifs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCommerciaux(data);
      }
    } catch (err) {
      console.error('Erreur chargement commerciaux:', err);
    }
  };

  useEffect(() => {
    setCurrentPage(1); // Réinitialiser la page
    fetchData(1);
  }, [activeTab]);

  // AUTO-IMPORT TOUTES LES 30 SECONDES (quasi temps réel) - OPTIMISÉ
  useEffect(() => {
    const autoImport = async () => {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API}/admin/trigger-import`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        // Rafraîchir silencieusement seulement la première page
        if (activeTab === 'prospects_entrants') {
          fetchData(1);
        }
      } catch (err) {
        console.log('Auto-import error:', err);
      }
    };

    // ⛔ AUTO-IMPORT DÉSACTIVÉ - Crée des doublons à chaque exécution
    // L'import automatique doit être géré par le scheduler backend uniquement
    // const initialTimer = setTimeout(autoImport, 10000);
    // const interval = setInterval(autoImport, 30 * 1000);

    // return () => {
    //   clearTimeout(initialTimer);
    //   clearInterval(interval);
    // };
  }, [activeTab]);

  const fetchData = async (page = 1, append = false) => {
    // Si on ajoute des prospects (pagination), ne montrer qu'un mini spinner
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Récupérer les données en fonction de l'onglet actif pour optimiser
      if (activeTab === 'prospects_entrants') {
        // Charger prospects avec pagination + stats + config
        const [prospectsRes, statsRes, configRes] = await Promise.all([
          fetch(`${API}/admin/prospects-entrants?page=${page}&limit=50`, { headers }),
          fetch(`${API}/admin/prospects-entrants/statistiques`, { headers }),
          fetch(`${API}/admin/config/dispatching`, { headers })
        ]);

        if (prospectsRes.ok && statsRes.ok && configRes.ok) {
          const prospectsResponse = await prospectsRes.json();
          const statsData = await statsRes.json();
          const configData = await configRes.json();

          // DEBUG: Logger les dates du premier prospect
          if (prospectsResponse.prospects && prospectsResponse.prospects.length > 0) {
            const firstProspect = prospectsResponse.prospects[0];
            console.log('Premier prospect chargé:', {
              nom: firstProspect.nom,
              date_lead: firstProspect.date_lead,
              date_reception: firstProspect.date_reception
            });
          }
          
          // Trier les prospects du plus récent au plus ancien (par date de réception/lead)
          const sortedProspects = prospectsResponse.prospects.sort((a, b) => {
            const parseDate = (d) => {
              if (!d) return new Date(0);
              if (typeof d === 'string' && d.match(/^\d{2}\/\d{2}\/\d{4}/)) {
                const parts = d.substring(0, 10).split('/');
                return new Date(parts[2], parts[1]-1, parts[0]);
              }
              return new Date(d);
            };
            const dateA = parseDate(a.date_lead || a.date_reception || a.created_at);
            const dateB = parseDate(b.date_lead || b.date_reception || b.created_at);
            return dateB - dateA; // Tri décroissant (plus récent en premier)
          });
          
          // Si append = true, ajouter à la liste existante
          if (append) {
            setProspects(prev => [...prev, ...sortedProspects]);
          } else {
            setProspects(sortedProspects);
          }
          
          setPagination(prospectsResponse.pagination);
          setStatistiques(statsData);
          setConfiguration({ jours_actifs: [], ...(configData.config || configData) });
          setError(null);
        } else {
          setError('Erreur lors du chargement des données');
        }
      } else if (activeTab === 'prospects') {
        // Charger seulement les clients pour l'onglet Prospects
        const clientsRes = await fetch(`${API}/clients`, { headers });
        if (clientsRes.ok) {
          const clientsData = await clientsRes.json();
          setClients(clientsData);
          setError(null);
        }
      } else if (activeTab === 'configuration') {
        // Charger seulement la configuration
        const configRes = await fetch(`${API}/admin/config/dispatching`, { headers });
if (configRes.ok) {
  const configData = await configRes.json();

  setConfiguration({
    jours_actifs: [],
    ...(configData.config || configData)
  });

  setError(null);
}
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const saveConfiguration = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/config/dispatching`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configuration)
      });

      if (response.ok) {
        setSuccess('Configuration sauvegardée avec succès !');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const autoSave = async (newConfig) => {
    try {
      const token = localStorage.getItem('token');
      fetch(`${API}/admin/config/dispatching`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newConfig)
      }).then(() => {
        setSuccess('✓ Sauvegardé');
        setTimeout(() => setSuccess(null), 1500);
      });
    } catch (e) { console.error('Autosave error:', e); }
  };

  const handleConfigChange = (field, value) => {
    setConfiguration(prev => {
      const newConfig = { ...prev, [field]: value };
      autoSave(newConfig);
      return newConfig;
    });
  };

  const handleJourToggle = (jour) => {
    setConfiguration(prev => {
      const joursActifs = prev?.jours_actifs || [];
      const newConfig = {
        ...prev,
        jours_actifs: joursActifs.includes(jour)
          ? joursActifs.filter(j => j !== jour)
          : [...joursActifs, jour].sort()
      };
      autoSave(newConfig);
      return newConfig;
    });
  };

  // Fonctions pour l'onglet Prospects
  const handleClientSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/clients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newClient)
      });

      if (response.ok) {
        await fetchData();
        setShowAddClientForm(false);
        setNewClient({
          nom: '',
          prenom: '',
          email: '',
          telephone: '',
          adresse: '',
          ville: '',
          code_postal: '',
          commercial_id: user?.id || '',
          surface_toit: '',
          consommation_annuelle: '',
          notes: ''
        });
        setSuccess('Prospect ajouté avec succès !');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Erreur lors de la création du prospect');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  const handleClientInputChange = (field, value) => {
    setNewClient(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const filteredClients = (Array.isArray(clients) ? clients : []).filter(client => {
    const matchesSearch = `${client.prenom} ${client.nom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.telephone.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const dispatcherProspect = async (prospectId) => {
    // Ouvrir modal pour sélection du commercial
    setProspectToDispatch(prospectId);
    setShowDispatchModal(true);
  };

  const handleManualDispatch = async () => {
    if (!selectedCommercial) {
      console.log('Veuillez sélectionner un commercial');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/assign/manual`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
  prospect_id: prospectToDispatch,
  commercial_id: selectedCommercial
})
      });

      if (response.ok) {
        const result = await response.json();
        console.log(result.message || 'Prospect assigné avec succès');
        setShowDispatchModal(false);
        setSelectedCommercial('');
        // Rafraîchir seulement la page actuelle au lieu de tout recharger
        await fetchData(currentPage);
      } else {
        setError('Erreur lors de l\'assignation');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  // FONCTION SIMPLE POUR NETTOYER LA BASE DE DONNÉES
  const handleClearAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/clear-all-prospects`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        await fetchData(); // Rafraîchir
      }
    } catch (err) {
      console.error('Erreur de connexion:', err);
    }
  };

  // FONCTION POUR IMPORTER LES LEADS DEPUIS GOOGLE SHEETS
  const handleImportLeads = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/trigger-import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          console.log('✅ Import terminé avec succès !\n\nConsultez les logs pour voir le détail.');
          await fetchData(); // Rafraîchir la liste
        } else {
          console.log('⚠️ Import terminé mais avec des erreurs.\n\nVérifiez les logs.');
        }
      } else if (response.status === 408) {
        console.log('⏱️ Import en cours...\n\nCela peut prendre du temps avec beaucoup de leads. Actualisez la page dans quelques instants.');
      } else {
        console.log('❌ Erreur lors de l\'import');
      }
    } catch (err) {
      console.log('❌ Erreur de connexion: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSourceIcon = (source) => {
    const sourceLower = source.toLowerCase();
    if (sourceLower.includes('facebook')) {
      return <Facebook className="w-4 h-4 text-blue-600" />;
    } else if (sourceLower.includes('google')) {
      return <Chrome className="w-4 h-4 text-green-600" />;
    } else if (sourceLower.includes('instagram')) {
      return <Instagram className="w-4 h-4 text-pink-600" />;
    } else if (sourceLower.includes('tiktok')) {
      return <Globe className="w-4 h-4 text-purple-600" />;
    } else {
      return <UserPlus className="w-4 h-4 text-gray-600" />;
    }
  };

  const getSourceLabel = (source) => {
    const sourceLower = source.toLowerCase();
    if (sourceLower.includes('facebook')) {
      return 'Facebook';
    } else if (sourceLower.includes('google')) {
      return 'Google';
    } else if (sourceLower.includes('instagram')) {
      return 'Instagram';
    } else if (sourceLower.includes('tiktok')) {
      return 'TikTok';
    } else if (sourceLower === 'autres') {
      return 'Site Web';
    } else {
      return 'Autres';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'en_attente':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'assigne':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'contacte':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'qualifie':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'converti':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'rejete':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'en_attente':
        return 'En attente';
      case 'assigne':
        return 'Assigné';
      case 'contacte':
        return 'Contacté';
      case 'qualifie':
        return 'Qualifié';
      case 'converti':
        return 'Converti';
      case 'rejete':
        return 'Rejeté';
      default:
        return 'Inconnu';
    }
  };

  const filteredProspects = (Array.isArray(prospects) ? prospects : []).filter(prospect => {
    if (filter === 'tous') return true;
    if (filter === 'en_attente') return prospect.status === 'en_attente';
    if (filter === 'assigne') return prospect.status === 'assigne';
    if (filter === 'facebook') return prospect.source === 'facebook_ads';
    if (filter === 'google') return prospect.source === 'google_ads';
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString || dateString === '') {
      return 'N/A';
    }
    
    try {
      // Vérifier si la date est au format DD/MM/YYYY ou DD/MM/YYYY HH:MM:SS
      if (typeof dateString === 'string' && dateString.match(/^\d{2}\/\d{2}\/\d{4}/)) {
        return dateString.substring(0, 10);
      }
      
      // Sinon, parser la date ISO ou autre format
      const date = new Date(dateString);
      
      // Vérifier si la date est valide
      if (isNaN(date.getTime())) {
        console.log('Date invalide reçue:', dateString);
        return 'N/A';
      }
      
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      console.error('Erreur parsing date:', dateString, e);
      return 'N/A';
    }
  };

  // Constantes pour la configuration
  const jours = [
    { id: 'lun', label: 'Lun', nom: 'Lundi' },
    { id: 'mar', label: 'Mar', nom: 'Mardi' },
    { id: 'mer', label: 'Mer', nom: 'Mercredi' },
    { id: 'jeu', label: 'Jeu', nom: 'Jeudi' },
    { id: 'ven', label: 'Ven', nom: 'Vendredi' },
    { id: 'sam', label: 'Sam', nom: 'Samedi' },
    { id: 'dim', label: 'Dim', nom: 'Dimanche' }
  ];

  const methodes = [
    {
      id: 'round_robin',
      nom: 'Round-Robin',
      description: 'Attribution équitable en alternance',
      icon: Users
    },
    {
      id: 'charge_travail',
      nom: 'Charge de travail',
      description: 'Selon le nombre de prospects actifs',
      icon: Target
    }
  ];

  const frequences = [
    { value: 1, label: 'Toutes les 1 minute' },
    { value: 15, label: 'Toutes les 15 minutes' },
    { value: 30, label: 'Toutes les 30 minutes' },
    { value: 60, label: 'Toutes les heures' },
    { value: 120, label: 'Toutes les 2 heures' },
    { value: 240, label: 'Toutes les 4 heures' }
  ];

  if (!user || user.role !== 'admin') {
    return (
      <div className="text-center py-20">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Accès non autorisé</h2>
        <p className="text-gray-600">Seuls les administrateurs peuvent accéder à cette page.</p>
      </div>
    );
  }

  if (loading && (!prospects || !prospects.length)) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mr-3" />
        <span className="text-lg text-gray-600">Chargement des prospects...</span>
      </div>
    );
  }

  return (
    <>
      {success && (
        <div style={{position:'fixed',top:100,right:24,zIndex:9999,background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:8,padding:'8px 16px',display:'flex',alignItems:'center',gap:8,fontSize:13,color:'#16a34a',boxShadow:'0 2px 8px rgba(0,0,0,0.08)',pointerEvents:'none'}}>
          <span>✓</span>
          <span>Sauvegardé</span>
        </div>
      )}
    <div className="space-y-6">
      {/* Header avec onglets */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Prospects</h1>
          <p className="text-gray-600">Réception automatique et dispatching intelligent</p>
        </div>
        
        <div className="flex items-center gap-4">
         

          
          

          
        </div>
      </div>

      {/* Onglets */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('prospects_entrants')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'prospects_entrants'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Prospects Entrants
                {statistiques && (
                  <span className="bg-blue-100 text-blue-600 py-1 px-2 rounded-full text-xs">
                    {statistiques.en_attente}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('prospects')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'prospects'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Prospects
                {clients && (
                  <span className="bg-green-100 text-green-600 py-1 px-2 rounded-full text-xs">
                    {clients.length}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('configuration')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'configuration'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Configuration Dispatching
                {configuration && (
                  <div className={`w-2 h-2 rounded-full ${configuration.actif ? 'bg-green-500' : 'bg-red-500'}`}></div>
                )}
              </div>
            </button>
          </nav>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-800">{error}</span>
        </div>
      )}



      {/* Contenu selon l'onglet actif */}
      {activeTab === 'prospects_entrants' && (
        <>
          {/* Statistiques */}
          {statistiques && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Prospects</p>
                <p className="text-2xl font-bold text-gray-900">{statistiques.total_prospects}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Non assignés</p>
                <p className="text-2xl font-bold text-yellow-600">{statistiques.en_attente ?? 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Assignés</p>
                <p className="text-2xl font-bold text-green-600">{statistiques.assignes}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sources de prospects */}
      {statistiques && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Répartition par Source
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(statistiques.par_source)
  .filter(([source]) => source !== 'autres' && source !== 'site_web')
  .map(([source, count]) => (
              <div key={source} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {getSourceIcon(source)}
                  <span className="font-medium">{getSourceLabel(source)}</span>
                </div>
                <span className="text-xl font-bold text-gray-900">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

  {/* Liste des prospects */}
<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
  <div className="px-6 py-4 border-b border-gray-200">
    <div className="flex items-center justify-between">
      <h2 className="text-lg font-semibold text-gray-900">
        Prospects Entrants ({filteredProspects.length})
      </h2>

      <p className="text-sm text-gray-600 whitespace-nowrap">
        Dernière mise à jour : {new Date().toLocaleTimeString('fr-FR')}
      </p>
    </div>
  </div>

        {filteredProspects.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Prospect
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Source
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Commercial
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Reçu le
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredProspects.map((prospect) => (
                  <tr key={prospect.id} className="hover:bg-gray-50">
                    {/* Colonne Prospect */}
                    <td className="px-6 py-4 text-center">
                      <div className="relative flex items-center justify-center">
                        {prospect.is_doublon && (() => {
                          const count = prospects.filter(p => p.telephone === prospect.telephone).length;
                          return (
                            <span style={{position:'absolute', left:0}} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-600 border border-orange-200">
                              {count}x
                            </span>
                          );
                        })()}
                        <div>
                          <p className="font-medium text-gray-900">
                            {prospect.prenom} {prospect.nom}
                          </p>
                          <div className="text-sm font-medium text-gray-900 flex items-center justify-center gap-1 mt-1">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="break-words">
                          {prospect.adresse && <span>{prospect.adresse}</span>}
                          {prospect.adresse && prospect.ville && ', '}
                          {prospect.ville && <span>{prospect.ville}</span>}
                          {prospect.ville && prospect.code_postal && ', '}
                          {prospect.code_postal && <span>{prospect.code_postal}</span>}
                        </span>
                        </div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Colonne Source (utm_source) */}
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        formatSource(prospect.source) === 'Google Ads' ? 'bg-green-100 text-green-800' :
                        formatSource(prospect.source) === 'Facebook' ? 'bg-blue-100 text-blue-800' :
                        formatSource(prospect.source) === 'Instagram' ? 'bg-pink-100 text-pink-800' :
                        formatSource(prospect.source) === 'Autre' ? 'bg-orange-100 text-orange-800' :
                        formatSource(prospect.source) === 'Inconnue' ? 'bg-gray-100 text-gray-600' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {formatSource(prospect.source)}
                      </span>
                    </td>
                    
                    {/* Colonne Contact */}
                    <td className="px-6 py-4 text-center">
                      <div>
                        <p className="text-sm text-gray-600 flex items-center justify-center gap-2">
                          <Mail className="w-3 h-3" />
                          {prospect.email}
                        </p>
                        <p className="text-sm text-gray-600 flex items-center justify-center gap-2 mt-1">
                          <Phone className="w-3 h-3" />
                          {formatPhoneNumber(prospect.telephone)}
                        </p>
                      </div>
                    </td>
                    
                    {/* Colonne Statut - Assigné / Non assigné */}
                    <td className="px-6 py-4 text-center">
                      {prospect.commercial_assigne_id ? (
                        <span className="text-sm font-medium text-green-600">
                          Assigné
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">
                          Non assigné
                        </span>
                      )}
                    </td>
                    
                    {/* Colonne Commercial - Nom du commercial */}
                    <td className="px-6 py-4 text-center">
                      {prospect.commercial_nom ? (
                        <span className="text-sm font-medium text-gray-900">
                          {prospect.commercial_nom}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 italic">
                          Non assigné
                        </span>
                      )}
                    </td>
                    
                    {/* Colonne Reçu le - Affiche date_lead (colonne A du Sheet) avec fallback sur date_reception */}
                    <td className="px-6 py-4 text-center text-sm text-gray-900">
                      {formatDate(prospect.date_lead || prospect.date_reception)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        {!prospect.commercial_assigne_id ? (
                          <button
                            onClick={() => dispatcherProspect(prospect.id)}
                            className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                          >
                            <Zap className="w-3 h-3" />
                            Assigner
                          </button>
                        ) : (
                          <button
                            onClick={() => dispatcherProspect(prospect.id)}
                            className="flex items-center gap-1 px-3 py-1 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                          >
                            <RefreshCw className="w-3 h-3" />
                            Changer
                          </button>
                        )}
                        <button
                          onClick={() => setSelectedProspect(prospect)}
                          className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <Eye className="w-3 h-3" />
                          Voir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Aucun prospect trouvé</p>
          </div>
        )}
        
        {/* Contrôles de pagination */}
        {filteredProspects.length > 0 && pagination.total_pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
            <div className="text-sm text-gray-600">
              Page {pagination.page} sur {pagination.total_pages} 
              <span className="ml-2">
                ({pagination.total} prospect{pagination.total > 1 ? 's' : ''} au total)
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const newPage = currentPage - 1;
                  setCurrentPage(newPage);
                  fetchData(newPage);
                }}
                disabled={currentPage === 1 || loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Précédent
              </button>
              
              {/* Afficher quelques numéros de page */}
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => {
                      setCurrentPage(pageNum);
                      fetchData(pageNum);
                    }}
                    disabled={loading}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      currentPage === pageNum
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              {pagination.total_pages > 5 && (
                <>
                  {currentPage < pagination.total_pages - 2 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                  {currentPage < pagination.total_pages - 1 && (
                    <button
                      onClick={() => {
                        setCurrentPage(pagination.total_pages);
                        fetchData(pagination.total_pages);
                      }}
                      disabled={loading}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {pagination.total_pages}
                    </button>
                  )}
                </>
              )}
              
              <button
                onClick={() => {
                  const newPage = currentPage + 1;
                  setCurrentPage(newPage);
                  fetchData(newPage);
                }}
                disabled={currentPage >= pagination.total_pages || loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Suivant
              </button>
            </div>
            
            {loadingMore && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Chargement...
              </div>
            )}
          </div>
        )}
      </div>
        </>
      )}

      {/* Onglet Prospects */}
      {activeTab === 'prospects' && (
        <div className="space-y-6">
          {/* Header avec bouton ajouter */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Gestion des Prospects</h2>
              <p className="text-gray-600">Liste et gestion de tous vos prospects</p>
            </div>
            <button
              onClick={() => setShowAddClientForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Ajouter Prospect
            </button>
          </div>

          {/* Barre de recherche et filtres */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Rechercher un prospect..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Tous les statuts</option>
                <option value="prospect">Prospect</option>
                <option value="client">Client</option>
                <option value="perdu">Perdu</option>
              </select>
            </div>
          </div>

          {/* Liste des prospects */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Prospects ({filteredClients.length})
              </h3>
            </div>

            {filteredClients.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Prospect
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Adresse
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredClients.map((client) => (
                      <tr key={client.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                              <span className="text-white text-sm font-medium">
                                {client.prenom?.charAt(0)}{client.nom?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {client.prenom} {client.nom}
                              </p>
                              <p className="text-sm text-gray-600">{client.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Phone className="w-3 h-3" />
                              {client.telephone}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Mail className="w-3 h-3" />
                              {client.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-3 h-3 text-gray-400" />
                              <span>{client.ville}</span>
                            </div>
                            <div className="text-gray-600">{client.code_postal}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            client.status === 'client' ? 'bg-green-100 text-green-800' :
                            client.status === 'prospect' ? 'bg-blue-100 text-blue-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {client.status || 'Prospect'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                              <Eye className="w-3 h-3" />
                              Voir
                            </button>
                            <button className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                              <Edit className="w-3 h-3" />
                              Modifier
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">
                  {searchTerm ? 'Aucun prospect trouvé pour cette recherche' : 'Aucun prospect trouvé'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Onglet Configuration */}
      {activeTab === 'configuration' && configuration && (
        <div style={{fontFamily: 'inherit'}}>



          {/* État du système */}
          <div style={{background:'white',border:'0.5px solid #e2e8f0',borderRadius:12,padding:'1.25rem',marginBottom:'1rem'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontSize:14,fontWeight:500,color:'#111',marginBottom:4}}>État du système</div>
                <div style={{fontSize:12,color:'#6b7280'}}>Activer ou désactiver le dispatching automatique</div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <span style={{background:configuration.actif?'#dcfce7':'#fee2e2',color:configuration.actif?'#15803d':'#b91c1c',fontSize:11,fontWeight:500,padding:'3px 10px',borderRadius:20}}>
                  {configuration.actif ? 'Actif' : 'Inactif'}
                </span>
                <div onClick={() => handleConfigChange('actif', !configuration.actif)} style={{width:44,height:24,background:configuration.actif?'#3b82f6':'#cbd5e1',borderRadius:12,position:'relative',cursor:'pointer',transition:'background 0.2s'}}>
                  <div style={{width:18,height:18,background:'white',borderRadius:'50%',position:'absolute',top:3,left:configuration.actif?23:3,transition:'left 0.2s'}} />
                </div>
              </div>
            </div>
          </div>

          {/* Méthode de dispatching */}
          <div style={{background:'white',border:'0.5px solid #e2e8f0',borderRadius:12,padding:'1.25rem',marginBottom:'1rem'}}>
            <div style={{fontSize:14,fontWeight:500,color:'#111',marginBottom:'0.75rem'}}>Méthode de dispatching</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              {methodes.map((methode) => (
                <div key={methode.id} onClick={() => handleConfigChange('methode', methode.id)}
                  style={{border: configuration.methode === methode.id ? '1.5px solid #3b82f6' : '0.5px solid #e2e8f0', borderRadius:8, padding:'10px 14px', cursor:'pointer', background: configuration.methode === methode.id ? '#eff6ff' : 'white'}}>
                  <div style={{fontSize:13,fontWeight:500,color: configuration.methode === methode.id ? '#1d4ed8' : '#111'}}>{methode.nom}</div>
                  <div style={{fontSize:11,color:'#6b7280',marginTop:2}}>{methode.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Jours + Heures */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem',marginBottom:'1rem'}}>
            <div style={{background:'white',border:'0.5px solid #e2e8f0',borderRadius:12,padding:'1.25rem'}}>
              <div style={{fontSize:14,fontWeight:500,color:'#111',marginBottom:'0.75rem'}}>Jours de dispatching</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(7, 1fr)',gap:6}}>
                {jours.map((jour) => (
                  <div key={jour.id} onClick={() => handleJourToggle(jour.id)}
                    style={{border:(configuration?.jours_actifs||[]).includes(jour.id)?'0.5px solid #3b82f6':'0.5px solid #e2e8f0',
                      background:(configuration?.jours_actifs||[]).includes(jour.id)?'#eff6ff':'transparent',
                      color:(configuration?.jours_actifs||[]).includes(jour.id)?'#3b82f6':'#6b7280',
                      borderRadius:8,padding:'7px 4px',textAlign:'center',fontSize:12,cursor:'pointer',
                      fontWeight:(configuration?.jours_actifs||[]).includes(jour.id)?500:400}}>
                    {jour.label}
                  </div>
                ))}
              </div>
            </div>
            <div style={{background:'white',border:'0.5px solid #e2e8f0',borderRadius:12,padding:'1.25rem'}}>
              <div style={{fontSize:14,fontWeight:500,color:'#111',marginBottom:'0.75rem'}}>Heures de dispatching</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div>
                  <div style={{fontSize:12,color:'#6b7280',marginBottom:4}}>Heure de début</div>
                  <select value={configuration.horaires_debut||'08:00'} onChange={(e) => handleConfigChange('horaires_debut', e.target.value)}
                    style={{width:'100%',border:'0.5px solid #e2e8f0',borderRadius:8,padding:'8px 10px',fontSize:13,background:'white',boxSizing:'border-box'}}>
                    {['06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00','22:00'].map(h => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={{fontSize:12,color:'#6b7280',marginBottom:4}}>Heure de fin</div>
                  <select value={configuration.horaires_fin||'19:00'} onChange={(e) => handleConfigChange('horaires_fin', e.target.value)}
                    style={{width:'100%',border:'0.5px solid #e2e8f0',borderRadius:8,padding:'8px 10px',fontSize:13,background:'white',boxSizing:'border-box'}}>
                    {['06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00','22:00'].map(h => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Quota + Fréquence */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem',marginBottom:'1rem'}}>
            <div style={{background:'white',border:'0.5px solid #e2e8f0',borderRadius:12,padding:'1.25rem'}}>
              <div style={{fontSize:14,fontWeight:500,color:'#111',marginBottom:'0.25rem'}}>Règle de quota</div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div style={{fontSize:13,color:'#6b7280'}}>Max leads "Nouveau prospect"</div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <button onClick={() => handleConfigChange('max_prospects_par_jour', Math.max(1,(configuration.max_prospects_par_jour||15)-1))}
                    style={{width:28,height:28,borderRadius:6,border:'0.5px solid #bfdbfe',background:'#eff6ff',color:'#3b82f6',fontSize:16,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>−</button>
                  <span style={{fontSize:18,fontWeight:600,color:'#1d4ed8',minWidth:28,textAlign:'center'}}>{configuration.max_prospects_par_jour||15}</span>
                  <button onClick={() => handleConfigChange('max_prospects_par_jour', Math.min(50,(configuration.max_prospects_par_jour||15)+1))}
                    style={{width:28,height:28,borderRadius:6,border:'0.5px solid #bfdbfe',background:'#eff6ff',color:'#3b82f6',fontSize:16,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>+</button>
                </div>
              </div>
            </div>
            <div style={{background:'white',border:'0.5px solid #e2e8f0',borderRadius:12,padding:'1.25rem'}}>
              <div style={{fontSize:14,fontWeight:500,color:'#111',marginBottom:'0.75rem'}}>Fréquence de dispatching</div>
              <select value={configuration.frequence_minutes} onChange={(e) => handleConfigChange('frequence_minutes', parseInt(e.target.value))}
                style={{width:'100%',border:'0.5px solid #e2e8f0',borderRadius:8,padding:'6px 10px',fontSize:13,background:'white'}}>
                {frequences.map((freq) => (
                  <option key={freq.value} value={freq.value}>{freq.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Commerciaux */}
          <div style={{background:'white',border:'0.5px solid #e2e8f0',borderRadius:12,padding:'1.25rem',marginBottom:'1rem'}}>
            <div style={{fontSize:14,fontWeight:500,color:'#111',marginBottom:'1rem'}}>Commerciaux</div>
            {commerciaux && commerciaux.length > 0 ? commerciaux.map((c, index) => {
              const max = configuration.max_prospects_par_jour || 15;
              const pct = Math.round(((c.charge_actuelle || 0) / max) * 100);
              const progressColor = pct >= 70 ? '#16a34a' : pct >= 50 ? '#3b82f6' : pct >= 30 ? '#f59e0b' : '#ef4444';
              const avatarColors = [{background:'#eff6ff',color:'#3b82f6'},{background:'#fffbeb',color:'#b45309'},{background:'#fef2f2',color:'#dc2626'},{background:'#f0fdf4',color:'#16a34a'},{background:'#fdf4ff',color:'#9333ea'}];
              const av = avatarColors[index % avatarColors.length];
              const initiales = `${(c.prenom||'')[0]||''}${(c.nom||'')[0]||''}`.toUpperCase();
              return (
                <div key={c.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 0',borderBottom:'0.5px solid #f1f5f9'}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:34,height:34,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:500,...av}}>{initiales}</div>
                    <div>
                      <div style={{fontSize:13,fontWeight:500,color:'#111'}}>{c.prenom} {c.nom}</div>
                      <div style={{fontSize:11,color:'#6b7280'}}>{c.charge_actuelle||0} / {max} leads actifs</div>
                    </div>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:80,height:6,background:'#f1f5f9',borderRadius:3,overflow:'hidden'}}>
                      <div style={{width:`${Math.min(pct,100)}%`,height:'100%',background:progressColor,borderRadius:3}} />
                    </div>
                    <span style={{fontSize:11,color:'#6b7280',minWidth:30}}>{pct}%</span>
                    <div onClick={() => handleConfigChange('commercial_actif_' + c.id, !c.actif)}
                      style={{width:36,height:20,background:c.actif!==false?'#3b82f6':'#cbd5e1',borderRadius:10,position:'relative',cursor:'pointer'}}>
                      <div style={{width:14,height:14,background:'white',borderRadius:'50%',position:'absolute',top:3,left:c.actif!==false?19:3,transition:'left 0.2s'}} />
                    </div>
                  </div>
                </div>
              );
            }) : (
              <div style={{fontSize:13,color:'#6b7280',textAlign:'center',padding:'1rem'}}>Aucun commercial trouvé</div>
            )}
          </div>
        </div>
      )}

      {/* Modal ajout prospect */}
      {showAddClientForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Ajouter un Prospect
                </h3>
                <button
                  onClick={() => setShowAddClientForm(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            <form onSubmit={handleClientSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prénom
                  </label>
                  <input
                    type="text"
                    required
                    value={newClient.prenom}
                    onChange={(e) => handleClientInputChange('prenom', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom
                  </label>
                  <input
                    type="text"
                    required
                    value={newClient.nom}
                    onChange={(e) => handleClientInputChange('nom', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={newClient.email}
                    onChange={(e) => handleClientInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    required
                    value={newClient.telephone}
                    onChange={(e) => handleClientInputChange('telephone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse
                </label>
                <input
                  type="text"
                  value={newClient.adresse}
                  onChange={(e) => handleClientInputChange('adresse', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ville
                  </label>
                  <input
                    type="text"
                    value={newClient.ville}
                    onChange={(e) => handleClientInputChange('ville', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code postal
                  </label>
                  <input
                    type="text"
                    value={newClient.code_postal}
                    onChange={(e) => handleClientInputChange('code_postal', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Surface toit (m²)
                  </label>
                  <input
                    type="number"
                    value={newClient.surface_toit}
                    onChange={(e) => handleClientInputChange('surface_toit', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Consommation annuelle (kWh)
                  </label>
                  <input
                    type="number"
                    value={newClient.consommation_annuelle}
                    onChange={(e) => handleClientInputChange('consommation_annuelle', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  rows="3"
                  value={newClient.notes}
                  onChange={(e) => handleClientInputChange('notes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddClientForm(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Ajouter Prospect
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de détails prospect */}
      {selectedProspect && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedProspect.prenom} {selectedProspect.nom}
                  </h3>
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    {getSourceIcon(selectedProspect.source)}
                    {getSourceLabel(selectedProspect.source)} - {formatDate(selectedProspect.date_reception)}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedProspect(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <AlertCircle className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <p className="text-gray-900">{selectedProspect.email || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Téléphone</label>
                  <p className="text-gray-900">{selectedProspect.telephone}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Adresse</label>
                  <p className="text-gray-900">{selectedProspect.adresse || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Code postal</label>
                  <p className="text-gray-900">{selectedProspect.code_postal || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Surface toit</label>
                  <p className="text-gray-900">{selectedProspect.surface_toit ? `${selectedProspect.surface_toit} m²` : '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Budget estimé</label>
                  <p className="text-gray-900">{selectedProspect.budget_estime ? `${selectedProspect.budget_estime} €` : '-'}</p>
                </div>
              </div>

              {selectedProspect.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Notes</label>
                  <p className="text-gray-900 mt-1">{selectedProspect.notes}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                {selectedProspect.status === 'en_attente' && (
                  <button
                    onClick={() => {
                      dispatcherProspect(selectedProspect.id);
                      setSelectedProspect(null);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    <Zap className="w-4 h-4" />
                    Dispatcher maintenant
                  </button>
                )}
                <button
                  onClick={() => setSelectedProspect(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DISPATCHING MANUEL */}
      {showDispatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Assigner le prospect</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner un commercial
              </label>
              <select
                value={selectedCommercial}
                onChange={(e) => setSelectedCommercial(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Choisir un commercial --</option>
                {commerciaux.map(comm => (
                  <option key={comm.id} value={comm.id}>
                    {comm.prenom} {comm.nom} ({comm.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDispatchModal(false);
                  setSelectedCommercial('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleManualDispatch}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Assigner
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default AdminProspects;